import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Filter, TrendingDown, DollarSign, BarChart3, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import FormInput from '../FormInput';
import Pagination from '../Pagination';
import api from '../api';

const SortiesTab = () => {
  const [sorties, setSorties] = useState([]);
  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedSortie, setSelectedSortie] = useState(null);
  const [filterCaisse, setFilterCaisse] = useState('');

  const [formData, setFormData] = useState({
    nature: 'SORTIE',
    type_mouvement: 'SALAIRE',
    montant: '',
    description: '',
    caisse: '',
    utilisateur: ''
  });

  const typeOptions = [
    { value: 'SALAIRE', label: 'Paiement salaire' },
    { value: 'FOURNITURE', label: 'Achat fourniture' },
    { value: 'MAINTENANCE', label: 'Frais maintenance' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  useEffect(() => {
    fetchSorties();
    fetchCaisses();
  }, []);

  const fetchSorties = async () => {
    try {
      setLoading(true);
      const response = await api.get('/finance/mouvements/');
      const sortiesData = response.data.filter(mouvement => mouvement.nature === 'SORTIE');
      setSorties(sortiesData);
    } catch (error) {
      console.error('Erreur lors du chargement des sorties:', error);
      toast.error('Erreur lors du chargement des sorties');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaisses = async () => {
    try {
      const response = await api.get('/finance/caisses/');
      setCaisses(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des caisses:', error);
      toast.error('Erreur lors du chargement des caisses');
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        utilisateur: 1 // TODO: Récupérer l'utilisateur connecté
      };

      if (modalMode === 'add') {
        await api.post('/finance/mouvements/', dataToSubmit);
        toast.success('Sortie ajoutée avec succès');
      } else {
        await api.put(`/finance/mouvements/${selectedSortie.id}/`, dataToSubmit);
        toast.success('Sortie modifiée avec succès');
      }

      fetchSorties();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde de la sortie');
    }
  };

  const handleEdit = (sortie) => {
    setSelectedSortie(sortie);
    setFormData({
      nature: sortie.nature,
      type_mouvement: sortie.type_mouvement,
      montant: sortie.montant,
      description: sortie.description || '',
      caisse: sortie.caisse_id,
      utilisateur: sortie.utilisateur_id
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette sortie ?')) {
      try {
        await api.delete(`/finance/mouvements/${id}/`);
        fetchSorties();
        toast.success('Sortie supprimée avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression de la sortie');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nature: 'SORTIE',
      type_mouvement: 'SALAIRE',
      montant: '',
      description: '',
      caisse: '',
      utilisateur: ''
    });
    setSelectedSortie(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalMode('add');
    setShowModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrage des données
  const filteredSorties = sorties.filter(sortie => {
    const matchSearch = sortie.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       sortie.type_mouvement_display?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCaisse = !filterCaisse || sortie.caisse_id.toString() === filterCaisse;
    return matchSearch && matchCaisse;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSorties.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSorties.length / itemsPerPage);

  const caisseOptions = caisses.map(caisse => ({
    value: caisse.id,
    label: `Caisse ${caisse.annee_academique_libelle || 'Non définie'} - ${formatCurrency(caisse.solde)}`
  }));

  // Calculer les statistiques
  const totalSorties = sorties.reduce((sum, sortie) => sum + sortie.montant, 0);
  const nombreSorties = sorties.length;
  const moyenneSorties = nombreSorties > 0 ? totalSorties / nombreSorties : 0;

  // Préparer les données pour les graphiques
  const sortiesByType = sorties.reduce((acc, sortie) => {
    const type = sortie.type_mouvement_display || sortie.type_mouvement;
    acc[type] = (acc[type] || 0) + sortie.montant;
    return acc;
  }, {});

  const pieData = Object.entries(sortiesByType).map(([type, montant]) => ({
    name: type,
    value: montant
  }));

  // Sorties par mois (derniers 6 mois)
  const sortiesByMonth = sorties.reduce((acc, sortie) => {
    const date = new Date(sortie.date_mouvement);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    acc[monthKey] = (acc[monthKey] || 0) + sortie.montant;
    return acc;
  }, {});

  const lineData = Object.entries(sortiesByMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6)
    .map(([month, montant]) => ({
      mois: new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
      montant
    }));

  const COLORS = ['#EF4444', '#F59E0B', '#8B5CF6', '#06B6D4', '#10B981', '#3B82F6'];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-lg shadow border border-red-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-500 mr-4">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Total Sorties</h3>
              <p className="text-2xl font-bold text-red-900">{formatCurrency(totalSorties)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-lg shadow border border-orange-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-500 mr-4">
              <TrendingDown className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-orange-800">Nombre de Sorties</h3>
              <p className="text-2xl font-bold text-orange-900">{nombreSorties}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-lg shadow border border-yellow-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-500 mr-4">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Moyenne par Sortie</h3>
              <p className="text-2xl font-bold text-yellow-900">{formatCurrency(moyenneSorties)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par type */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Type</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Aucune donnée disponible
            </div>
          )}
        </div>

        {/* Évolution mensuelle */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution Mensuelle</h3>
          {lineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={lineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mois" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Line type="monotone" dataKey="montant" stroke="#EF4444" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Aucune donnée disponible
            </div>
          )}
        </div>
      </div>

      {/* Header avec bouton d'ajout et recherche */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une sortie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={filterCaisse}
              onChange={(e) => setFilterCaisse(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            >
              <option value="">Toutes les caisses</option>
              {caisses.map(caisse => (
                <option key={caisse.id} value={caisse.id}>
                  {caisse.annee_academique_libelle || 'Non définie'}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle Sortie
        </button>
      </div>

      {/* Tableau des sorties */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    Aucune sortie trouvée
                  </td>
                </tr>
              ) : (
                currentItems.map((sortie) => (
                  <tr key={sortie.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(sortie.date_mouvement)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {sortie.type_mouvement_display}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                      -{formatCurrency(sortie.montant)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {sortie.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(sortie)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(sortie.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Modal d'ajout/édition */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === 'add' ? 'Nouvelle Sortie' : 'Modifier la Sortie'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Type de mouvement"
            type="react-select"
            options={typeOptions}
            value={formData.type_mouvement}
            onChange={(value) => handleInputChange('type_mouvement', value)}
            required
          />

          <FormInput
            label="Montant (FCFA)"
            type="number"
            value={formData.montant}
            onChange={(e) => handleInputChange('montant', e.target.value)}
            min="0"
            required
          />

          <FormInput
            label="Caisse"
            type="react-select"
            options={caisseOptions}
            value={formData.caisse}
            onChange={(value) => handleInputChange('caisse', value)}
            required
          />

          <FormInput
            label="Description"
            type="textarea"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            required
          />

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
            >
              {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SortiesTab; 